import { useProjectStore } from '@/core/project/store/project.store'
import { SpEditorProvider } from '@/features/screenplay/components/editor/SpEditorProvider'
import HeadlessSpEditor from '@/features/shared/component/HeadlessSpEditor'
import { useCharactersStore } from '@/features/shared/stores/characters.store'
import { WorkspaceLayout } from '@/features/workspace'
import { Button } from '@/shared/components/ui/button'
import { Loader2, UserPlus, Users } from 'lucide-react'
import ChOptionBar from '../components/ChOptionBar'
import ChSidebar from '../components/ChSidebar'
import { useCharacters } from '../queries/character.queries'

import { useNavigate, useParams } from 'react-router-dom'
import { CharacterCard } from '../components/CharacterCard'
import CharacterHeader from '../components/CharacterHeader'

const CharacterProviderWrapper = ({ children }: { children: React.ReactNode }) => (
    <SpEditorProvider>{children}</SpEditorProvider>
)

export default function CharacterPage() {
    const { project_id } = useParams<{ project_id: string }>()
    const navigate = useNavigate()
    const { characters } = useCharactersStore()
    const { isLoading } = useCharacters(project_id || '')

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        )
    }

    return (
        <WorkspaceLayout
            ProviderWrapper={CharacterProviderWrapper}
            SidebarComponent={<ChSidebar />}
            optionBar={<ChOptionBar />}
        >
            <HeadlessSpEditor />
            <div className="flex-1 flex flex-col h-full bg-muted/10 overflow-hidden">
                {/* Standardized Header */}
                <CharacterHeader
                    characterCount={characters.length}
                    onAddCharacter={() => navigate(`/workspace/${project_id}/characters/new`)}
                    showAddButton={characters.length > 0}
                />

                <div className="flex-1 overflow-y-auto p-8 pt-4">
                    <div className="max-w-7xl mx-auto w-full min-h-0">
                        {characters.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed rounded-xl bg-background/50">
                                <Users className="w-16 h-16 text-muted-foreground/20 mb-4" />
                                <h3 className="text-xl font-semibold">No characters yet</h3>
                                <p className="text-muted-foreground mb-6">Start by adding your first character or import them from your screenplay.</p>
                                <Button onClick={() => navigate(`/workspace/${project_id}/characters/new`)}>
                                    <UserPlus className="w-4 h-4 mr-2" />
                                    Add First Character
                                </Button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 pb-12">
                                {characters.map(char => (
                                    <CharacterCard
                                        key={char.id}
                                        character={char}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </WorkspaceLayout >
    )
}
